import React, { FC, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import State from '@wfp-root-app/store/state';

import { CheckBoxContainer } from '../utils/utils';
import { UaopFlags } from '../../DryRunDialog';
import { useStepperContext } from '../StepperContext';

interface HouseholdExclusionStepProps {
    selectedUaop: UaopFlags;
    selectedAssistanceCategory: string;
    setDeduplicationCategories: (categories: ((prevCategories) => string[]) | string[]) => void;
    deduplicationCategories: string[];
    categoriesToSelect: string[];
    forcedCategoriesToDeduplicate?: string[];
    recommendedCategoryForSharedDeduplication?: string;
}

const AdditionalCategoriesStep: FC<HouseholdExclusionStepProps> = (props) => {
    const { disableNextStep, enableNextStep } = useStepperContext();
    const { categoriesToCategoriesFullNames } = useSelector((state: State) => state.appConfig.entitlementsConfig);
    const [noneSelected, setNoneSelected] = useState(false);

    useEffect(() => {
        props.deduplicationCategories.length > 0 || noneSelected ? enableNextStep() : disableNextStep();
    }, [props.deduplicationCategories, noneSelected]);

    const getUniqueCategories = (selectedCategories: string[], forcedCategories?: string[]) => {
        if (!forcedCategories) return selectedCategories;
        return Array.from(new Set([...selectedCategories, ...forcedCategories]));
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value, checked } = event.target;
        setNoneSelected(false);

        if (checked) {
            props.setDeduplicationCategories((prevCategories) =>
                getUniqueCategories([...prevCategories, value], props.forcedCategoriesToDeduplicate)
            );
        } else {
            props.setDeduplicationCategories((prevCategories) =>
                prevCategories.filter((category) => category !== value)
            );
        }
    };
    const handleNoneCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target;

        if (checked) {
            setNoneSelected(true);
            props.setDeduplicationCategories(
                props.forcedCategoriesToDeduplicate ? props.forcedCategoriesToDeduplicate : []
            );
        } else {
            setNoneSelected(false);
        }
    };

    const categoriesToShow = useMemo(() => {
        const filteredCategoriesToSelect = props.forcedCategoriesToDeduplicate
            ? props.categoriesToSelect.filter((category) => {
                  return !props.forcedCategoriesToDeduplicate.includes(category);
              })
            : props.categoriesToSelect;

        return [
            ...props.forcedCategoriesToDeduplicate.map((category) => ({
                name: category,
                disabledCheckbox: true,
            })),
            ...filteredCategoriesToSelect.map((category) => ({
                name: category,
                disabledCheckbox: false,
            })),
        ];
    }, [props.categoriesToSelect, props.recommendedCategoryForSharedDeduplication]);

    const shouldShowNoneCheckbox = useMemo(
        () =>
            categoriesToShow &&
            categoriesToShow.filter((categoryToShow) => !categoryToShow.disabledCheckbox).length > 0,
        [categoriesToShow]
    );

    return (
        <div className={'wfp-assisted-exclusion-step'}>
            <h6
                style={{
                    marginTop: '32px',
                }}
            >
                Do you also want to exclude households who have been assisted in any of the following assistance
                categories?
                <br />
                Select all that apply:
            </h6>
            <div>
                {categoriesToShow.map(({ name: categoryName, disabledCheckbox }) => {
                    return (
                        <CheckBoxContainer key={categoryName} marginTop="0px">
                            <label>
                                <input
                                    checked={props.deduplicationCategories.includes(categoryName)}
                                    className={'mr-2'}
                                    data-testid={`import.dedupCategory.${categoryName}`}
                                    disabled={disabledCheckbox}
                                    name="validate-category"
                                    onChange={handleCheckboxChange}
                                    type="checkbox"
                                    value={categoryName}
                                />{' '}
                                {categoriesToCategoriesFullNames[categoryName]} ({categoryName}){' '}
                                {categoryName === props.recommendedCategoryForSharedDeduplication
                                    ? '(Recommended)'
                                    : ''}
                            </label>
                        </CheckBoxContainer>
                    );
                })}
                {shouldShowNoneCheckbox && (
                    <CheckBoxContainer marginTop="0px">
                        <label>
                            <input
                                checked={noneSelected}
                                className={'mr-2'}
                                data-testid="import.dedupCategory.none"
                                name="validate-category"
                                onChange={handleNoneCheckboxChange}
                                type="checkbox"
                                value="none"
                            />{' '}
                            None of the above {props.forcedCategoriesToDeduplicate && 'optional'}
                        </label>
                    </CheckBoxContainer>
                )}
            </div>
        </div>
    );
};

AdditionalCategoriesStep.displayName = 'AdditionalCategoriesStep';

export { AdditionalCategoriesStep };
